import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import {
  POSTHOG_FEATURE_FLAGS,
  useAuthState,
  useCausesPortalAnalytics,
  useIsStatusCheckSignUp,
  useNotificationBar,
  usePostHogFeatureFlag,
  usePostHogFeatureFlagVariant,
  useSitewideMessage,
  useSocialImpactOrg
} from '@percent/cause-dashboard/common/hooks'
import { useCanUseGrants } from '@percent/cause-dashboard/common/hooks/canUseGrants/canUseGrants'
import { useCanUseVolunteering } from '@percent/cause-dashboard/common/hooks/canUseVolunteering/canUseVolunteering'
import {
  getMenuItemId,
  SideMenuEventName
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/causeDashboardAnalytics.types'
import { RESTRICTED_COUNTRIES_LIST } from '@percent/cause-dashboard/constants/donationDisallowedCountries'
import { GOODSTACK_PRO_LOGIN_PATH } from '@percent/cause-dashboard/constants/goodstackPro'
import { selectAuthState } from '@percent/cause-dashboard/context/auth'
import { createChatTargets } from '@percent/cause-dashboard/services/aichat/aichat'
import type { NavItemBadge, NavItemProps } from '@percent/lemonade'
import { Button, Flex, Icon, Logo, Navigation } from '@percent/lemonade'
import classNames from 'classnames/bind'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import styles from './SideMenu.module.scss'
import { LocalePicker } from '@percent/cause-dashboard/common/components'
import { SidebarGrantPromo } from './SidebarGrantPromo/SidebarGrantPromo'

const cx = classNames.bind(styles)

export interface SideMenuProps {
  isMobileLayout?: boolean
  mobileMenuOpen?: boolean
  onCloseMobileMenu?: () => void
}

export function SideMenu({ isMobileLayout, mobileMenuOpen, onCloseMobileMenu }: SideMenuProps = {}) {
  const { t } = useTranslation()
  const { track } = useCausesPortalAnalytics()
  const { authState } = useAuthState()
  const { claimAccepted } = selectAuthState(authState)
  const showDonationForm = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_DONATION_FORM_APPLICATION)
  const location = useLocation()
  const notification = useNotificationBar()
  const { shouldShowSitewideMessage } = useSitewideMessage()
  const canUseVolunteering = useCanUseVolunteering()
  const canUseGrants = useCanUseGrants()
  const isStatusCheckSignUp = useIsStatusCheckSignUp()
  const grantFinderSidebarFlagKey = POSTHOG_FEATURE_FLAGS.CP_SIDEBAR_GOTO_GRANT_FINDER_COPY_EXPERIMENT
  const grantFinderCopyVariant = usePostHogFeatureFlagVariant(grantFinderSidebarFlagKey)
  const showGrantAssistantSidebarLabel = !canUseGrants && grantFinderCopyVariant === 'test'
  const isGoodstackProLoginRedirect = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_GOODSTACK_PRO_LOGIN_REDIRECT)
  const showGrantAdvisoryPromo = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_GRANT_ADVISORY_CALENDLY)

  const shiftDownToAccomodateNotification = notification.isOpened || shouldShowSitewideMessage ? 48 : 0

  const orgCountryCode = authState.organisation?.countryCode || authState.claimOrganisation?.countryCode
  const isDonationFormRestricted = orgCountryCode && RESTRICTED_COUNTRIES_LIST.includes(orgCountryCode)

  const { isSocialImpactOrg, socialImpactRoutes } = useSocialImpactOrg()

  const menuConfig: (Omit<NavItemProps, 'isActive'> & { claimed?: boolean; badge?: NavItemBadge })[] = [
    {
      label: t('menu.home'),
      href: RoutePath.HOME,
      icon: 'menu-dashboard'
    },
    {
      label: t('menu.charityProfile'),
      href: RoutePath.PROFILE,
      icon: 'menu-profile',
      claimed: isStatusCheckSignUp || undefined
    },
    {
      label: t('menu.donations'),
      href: RoutePath.DONATIONS,
      icon: 'menu-donations',
      claimed: true
    },
    ...(canUseGrants
      ? [
          {
            label: t('menu.grants'),
            href: RoutePath.GRANTS,
            icon: 'menu-grants' as const
          }
        ]
      : []),

    ...(showDonationForm
      ? [
          {
            label: t('menu.donationForm'),
            href: RoutePath.DONATION_FORM,
            icon: 'menu-donation-form' as const,
            claimed: true
          }
        ]
      : []),
    {
      label: t('menu.payouts'),
      href: RoutePath.PAYOUTS,
      icon: 'menu-payouts',
      claimed: true
    },
    {
      label: t('menu.productOffers'),
      href: RoutePath.PRODUCT_OFFERS,
      icon: 'menu-product-offers'
    },
    {
      label: t('menu.savingsCalculator'),
      href: RoutePath.SAVINGS_CALCULATOR,
      icon: 'matched-funds'
    },
    {
      label: t('menu.myDiscounts'),
      href: RoutePath.MY_DISCOUNTS,
      icon: 'menu-my-discounts'
    },
    {
      label: showGrantAssistantSidebarLabel ? t('menu.grantAssistant') : t('menu.goodstackPro'),
      ...(isGoodstackProLoginRedirect
        ? { href: GOODSTACK_PRO_LOGIN_PATH, reloadDocument: true }
        : { href: `${RoutePath.AI_GATEWAY}?target=${createChatTargets.goodstackGrants}` }),
      icon: 'reward-stars'
    },
    ...(canUseVolunteering.canUseVolunteering
      ? [
          {
            label: t('menu.volunteering'),
            href: RoutePath.VOLUNTEERING,
            icon: 'volunteering-calendar' as const
          }
        ]
      : [])
  ]

  const menuItems = menuConfig
    .filter(item => !item.claimed || claimAccepted)
    .filter(item => !isSocialImpactOrg || socialImpactRoutes.includes(item.href))
    .filter(item => {
      if (item.href === RoutePath.DONATION_FORM && isDonationFormRestricted) {
        return false
      }
      return true
    })
    .map<NavItemProps>(item => ({
      ...item,
      isActive: location.pathname.startsWith(item.href)
    }))

  useEffect(() => {
    if (!isMobileLayout || !mobileMenuOpen) {
      return
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isMobileLayout, mobileMenuOpen])

  useEffect(() => {
    if (!isMobileLayout || !mobileMenuOpen) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseMobileMenu?.()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isMobileLayout, mobileMenuOpen, onCloseMobileMenu])

  const handleItemClick = (item: { label: string; href: string }) => {
    track(SideMenuEventName.SIDE_MENU_ITEM_CLICKED, {
      menu_item: item.label,
      href: item.href,
      menu_item_id: getMenuItemId(item.href),
      accountId: authState.user?.id,
      organisationName: authState.organisation?.displayName ?? authState.claimOrganisation?.displayName,
      organisationId: authState.organisation?.id ?? authState.claimOrganisation?.id
    })
    if (isMobileLayout) {
      onCloseMobileMenu?.()
    }
  }

  const navClassName = cx(
    styles.sideMenu,
    shiftDownToAccomodateNotification && styles.notificationShift,
    mobileMenuOpen && styles.sideMenuMobileOpen
  )

  return (
    <div id="cause-dashboard-side-nav">
      {isMobileLayout && mobileMenuOpen ? (
        <button
          type="button"
          className={styles.backdrop}
          aria-label={t('menu.closeNavigationMenu')}
          data-testid="mobile-menu-backdrop"
          onClick={() => onCloseMobileMenu?.()}
        />
      ) : null}
      {isMobileLayout ? (
        <div
          className={cx(
            styles.mobileMenuPanelFrame,
            shiftDownToAccomodateNotification && styles.mobileMenuPanelFrameNotification,
            mobileMenuOpen && styles.mobileMenuPanelFrameOpen
          )}
        >
          <div className={styles.panelClose}>
            <Button
              variant="tertiary"
              aria-label={t('menu.closeNavigationMenu')}
              onClick={() => onCloseMobileMenu?.()}
              data-testid="mobile-menu-close"
            >
              <Icon name="close" size={5} color="black" />
            </Button>
          </div>
        </div>
      ) : null}
      <Navigation
        className={navClassName}
        items={menuItems}
        logo={<Logo variant="causes" />}
        onItemClick={handleItemClick}
      >
        <Flex direction="column" gap={16} mt="auto">
          {!canUseGrants && showGrantAdvisoryPromo && <SidebarGrantPromo />}
          <LocalePicker variant="sideMenu" />
        </Flex>
      </Navigation>
    </div>
  )
}
